import type { CaseStudy } from '../types';

export const shenzhen: CaseStudy = {
  id: 'shenzhen',
  order: 18,
  title: '深圳经济特区',
  english: 'Shenzhen SEZ',
  category: 'history',
  era: '1980 — 今',
  region: '中国 / 广东',
  accent: '#5f6e4e',
  hook: '一个边陲农业县在几十年里变成制造业与创新集群。但这不是“政策一放开就自动发生”的故事，而是土地、基础设施、移民与产业链共同长出来的结果。',
  question: '为什么同样的开放政策，在有些地方长出了产业集群，在另一些地方却没有？',
  summary:
    '深圳经济特区把土地制度、外资政策、基础设施、移民劳动力与香港的邻近性组合在一起，形成了制造业集群。后来，成本上升与产业升级又迫使它从代工转向研发与品牌。',
  thesis: '制度不是一张文件，而是一组能让土地、资本、劳动力和知识重新组合的接口。',
  quote: '特区真正稀缺的不是优惠政策，而是把政策变成基础设施、供应链和城市能力的能力。',
  isNew: true,
  entryNodeIds: ['sz-policy', 'sz-land', 'sz-infrastructure'],
  focusNodeId: 'sz-land',
  lensIds: ['public-goods', 'regulation', 'scale', 'scarcity', 'collective-action'],
  opportunityIds: ['opp-local-cluster'],
  sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
  facts: [
    {
      text: '1980 年 8 月，深圳经济特区正式成立；它成为中国改革开放早期最重要的制度试验场之一。',
      sourceIds: ['shenzhen-gov'],
    },
    {
      text: '1987 年 12 月，深圳公开拍卖了一宗国有土地的使用权，常被视为中国土地市场化的重要节点。',
      sourceIds: ['shenzhen-gov'],
    },
    {
      text: '深圳 GDP 从 1980 年的约 2.7 亿元增长到 2023 年的超过 3.4 万亿元（名义值，官方统计口径）；同期人口也从数十万增长到千万级。',
      sourceIds: ['shenzhen-gov'],
    },
  ],
  nodes: [
    {
      id: 'shenzhen',
      caseId: 'shenzhen',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '深圳经济特区',
      summary: '从制度试验到制造业与科技集群。',
      detail:
        '深圳的起点不是一张空白纸。它邻近香港、拥有港口与土地，但缺乏资本、技术与成熟产业。特区政策降低了外资进入、土地开发和劳动力流动的制度摩擦，让这些要素在短时间内重新组合。',
      boundary: '深圳的经验高度依赖香港、全球产业转移、中国整体改革与出口市场；不能简单复制到任何城市。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['shenzhen-gov'],
    },
    {
      id: 'sz-policy',
      caseId: 'shenzhen',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '特区政策：降低制度摩擦',
      summary: '外资、税收、土地、用工与审批的特殊安排，降低了企业进入和试错的成本。',
      detail:
        '特区不是“没有规则”，而是用不同的规则组合来吸引资本与技术。税收优惠、外资审批便利、土地供应和劳动用工改革，让企业可以在较短时间内建厂、招人、出口。政策的关键不是单一优惠，而是减少环节之间的摩擦。',
      boundary: '优惠政策会被其他地方模仿，边际吸引力下降；如果没有基础设施和供应链，单纯减税效果有限。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
    },
    {
      id: 'sz-land',
      caseId: 'shenzhen',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '土地：把使用权变成资本',
      summary: '土地有偿使用与拍卖，让政府获得基础设施资金，也让企业获得可交易的场地。',
      detail:
        '在传统体制下，土地难以定价、流转和抵押。深圳探索土地有偿使用、拍卖和成片开发，把土地从“行政划拨的资源”变成可以融资、招商和建设的资产。土地财政与基础设施投资由此形成循环。',
      boundary: '土地金融也会推高房价、积累债务，并挤压制造业与居民部门；它不是无成本的增长引擎。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
    },
    {
      id: 'sz-infrastructure',
      caseId: 'shenzhen',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '基础设施：先修路，再等产业',
      summary: '港口、道路、电力、通信与工业园区，降低了企业进入和物流成本。',
      detail:
        '制造业需要可靠的电、水、道路、港口和通关。基础设施投资往往先于需求，短期内看似浪费，但为后来的产业集群提供了容量。深圳的港口与口岸，使它能把“进口零件—加工—出口”连成一条高效链路。',
      boundary: '超前投资如果缺乏产业需求，会变成债务与空置；基础设施必须与产业和人口同步。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['worldbank-china-sez'],
    },
    {
      id: 'sz-migration',
      caseId: 'shenzhen',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '移民：把劳动力变成城市',
      summary: '大量外来务工者提供了低成本、可训练的劳动力，也创造了消费与住房需求。',
      detail:
        '深圳的制造业需要大量年轻劳动力。户籍、住房与公共服务制度影响了移民的长期定居，但“来了就是深圳人”的城市叙事与就业机会吸引了持续流入。人口规模又反过来支撑基础设施与服务业。',
      boundary: '低成本的劳动力优势会随工资、房价和人口结构变化而减弱，产业必须升级或转移。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['shenzhen-gov'],
    },
    {
      id: 'sz-cluster',
      caseId: 'shenzhen',
      kind: 'mechanism',
      stage: 2,
      lane: 0,
      title: '集群：供应商、人才与知识近距离碰撞',
      summary: '电子制造、硬件创业与软件企业聚集，降低了协作与试错成本。',
      detail:
        '在深圳，找到模具厂、元器件供应商、工程师和代工厂可能只需要几天。集群让知识溢出、招聘和打样更快，也让新企业可以专注于设计与销售，把重资产环节外包。到 2000 年代，华为、腾讯、大疆等企业又把制造能力与研发、品牌结合。',
      boundary: '集群也会带来租金上涨、同质化竞争和对少数龙头企业的依赖。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
    },
    {
      id: 'sz-outcome',
      caseId: 'shenzhen',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：增长、升级与高成本',
      summary: '从代工与出口加工，转向研发、品牌与高附加值服务，同时承受高房价与成本上升。',
      detail:
        '深圳从“三来一补”起步，逐步形成电子信息、通信、硬件创业与金融等产业。土地与房价上涨、劳动力成本上升，迫使低端制造外迁，也推动企业向研发、设计和品牌升级。增长与不平等、居住成本同时存在。',
      boundary: '深圳的人均 GDP 与创新指标不能代表所有居民的生活质量；城市内部差异很大。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
    },
    {
      id: 'sz-risk',
      caseId: 'shenzhen',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：土地金融与成本挤压',
      summary: '土地财政、房价与产业成本互相强化，可能把制造业和年轻人挤出。',
      detail:
        '土地出让收入支持基础设施，但高地价也会推高企业和居民成本。当制造业利润无法覆盖租金与工资，企业会外迁；当年轻人无法负担住房，城市吸引力下降。增长的成功本身会制造新的约束。',
      boundary: '城市可以通过保障房、产业空间、交通与税收政策缓解成本压力，但很难完全逆转。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['shenzhen-gov'],
    },
    {
      id: 'sz-opp',
      caseId: 'shenzhen',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：给产业集群做“接口服务”',
      summary: '当供应链足够密，机会往往在打样、合规、物流、招聘、跨境与空间运营等接口环节。',
      detail:
        '普通人不必造芯片或做品牌，也可以服务集群：快速打样、小批量生产、产品认证、跨境物流、产业空间运营、技术工人培训、供应链金融。关键是理解集群里“谁缺什么接口”，并用标准化服务降低交易成本。',
      boundary: '接口服务竞争激烈、毛利薄，且高度依赖集群景气；需要长期客户关系与专业化。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['shenzhen-gov', 'worldbank-china-sez'],
    },
  ],
  edges: [
    {
      id: 'sz-e1',
      caseId: 'shenzhen',
      from: 'shenzhen',
      to: 'sz-policy',
      label: '制度试验',
      explanation: '特区获得外资、土地、用工与审批方面的特殊安排。',
      tone: 'cause',
    },
    {
      id: 'sz-e2',
      caseId: 'shenzhen',
      from: 'shenzhen',
      to: 'sz-land',
      label: '土地改革',
      explanation: '土地有偿使用与拍卖，让土地成为可融资、可招商的资产。',
      tone: 'cause',
    },
    {
      id: 'sz-e3',
      caseId: 'shenzhen',
      from: 'sz-policy',
      to: 'sz-infrastructure',
      label: '政策引导投资',
      explanation: '特区政策与土地收入支持港口、道路、电力与园区建设。',
      tone: 'cause',
    },
    {
      id: 'sz-e4',
      caseId: 'shenzhen',
      from: 'sz-land',
      to: 'sz-infrastructure',
      label: '土地金融',
      explanation: '土地出让与开发收益，为基础设施提供资金。',
      tone: 'cause',
    },
    {
      id: 'sz-e5',
      caseId: 'shenzhen',
      from: 'sz-policy',
      to: 'sz-migration',
      label: '就业与准入',
      explanation: '外资与制造业扩张创造就业，吸引外来劳动力流入。',
      tone: 'cause',
    },
    {
      id: 'sz-e6',
      caseId: 'shenzhen',
      from: 'sz-infrastructure',
      to: 'sz-cluster',
      label: '物流与园区',
      explanation: '港口、道路与工业园区降低企业协作与出口成本，促进集群形成。',
      tone: 'enable',
    },
    {
      id: 'sz-e7',
      caseId: 'shenzhen',
      from: 'sz-migration',
      to: 'sz-cluster',
      label: '劳动力与消费',
      explanation: '移民提供可训练劳动力，也形成本地消费与住房需求。',
      tone: 'cause',
    },
    {
      id: 'sz-e8',
      caseId: 'shenzhen',
      from: 'sz-cluster',
      to: 'sz-outcome',
      label: '产业升级',
      explanation: '集群让企业从代工走向研发、品牌与硬件创业。',
      tone: 'cause',
    },
    {
      id: 'sz-e9',
      caseId: 'shenzhen',
      from: 'sz-outcome',
      to: 'sz-risk',
      label: '成功制造新成本',
      explanation: '房价、工资与土地价格上升，挤压低利润制造与年轻人。',
      tone: 'risk',
    },
    {
      id: 'sz-e10',
      caseId: 'shenzhen',
      from: 'sz-risk',
      to: 'sz-opp',
      label: '成本压力产生服务需求',
      explanation: '企业需要更高效的打样、合规、物流与空间服务来消化成本。',
      tone: 'enable',
    },
  ],
};
